/**
 * ชนิดข้อมูลของใบขอซื้อ (Purchase Requisition) — เขียนตาม payload contract ที่
 * freeze แล้ว (`docs/API_CONTRACT.md` — "Purchase Requisition — Payload Contract
 * (D-084)")
 *
 * ใบขอซื้อต่างจากทะเบียนผู้ขายที่อยู่ข้าง ๆ กันในเมนูสองข้อที่กำหนดทั้งหน้าจอ:
 *
 * 1. **มีขอบเขตโรงงาน** (D-004) — ใบขอซื้อหนึ่งใบเป็นของโรงงานเดียว เซิร์ฟเวอร์
 *    กรองรายการตาม `plant_scope` ของผู้เรียกเสมอ และโรงงานนอกขอบเขตตอบ `403`
 *    ไม่ใช่รายการว่าง ส่วนทะเบียนผู้ขายเป็นข้อมูลระดับองค์กรที่ไม่มี `plant_id` เลย
 * 2. **มี state machine** — `DRAFT → SUBMITTED → APPROVED → CONVERTED_TO_PO` และ
 *    `DRAFT|SUBMITTED → CANCELLED` การเปลี่ยนสถานะเกิดจาก route ของมันเอง
 *    (`submit`/`approve`/`cancel`) ไม่ใช่การ `PATCH` ฟิลด์ `status` — **ไม่มี
 *    `PATCH` และไม่มี `DELETE` ในกลุ่มนี้เลย**
 *
 * `pr_number` เป็น **business key ที่ผู้ใช้กรอกเอง** และแก้ไม่ได้หลังสร้าง
 * (ใบสั่งซื้อของ P2-2b อ้างเลขนี้) รหัสที่ซ้ำถูกตอบ `409 PR_NUMBER_TAKEN` โดยไม่มี
 * อะไรถูกเขียน
 *
 * ฟิลด์ที่ contract เรียกว่า **server-owned** (`pr_id`, `status`, ผู้สร้าง/ผู้อนุมัติ
 * และ timestamp ทั้งสี่) ไม่ปรากฏใน `CreatePurchaseRequisitionBody` เลยแม้แต่คีย์
 * เดียว — ไม่ใช่แค่ "ไม่ส่ง" แต่ **ไม่มีที่ให้ใส่** (เทียบ `UpdateSupplierBody` ที่
 * ไม่มี `supplier_code` ด้วยเหตุผลเดียวกัน)
 */

/** รหัสโมดูลของหน้าจอนี้ใน RBAC matrix — โมดูล **ธุรกิจ** ปกติ ADMIN ถูก deny (D-060) */
export const PROCUREMENT_MODULE = 'PROCUREMENT';

/* ------------------------------------------------------------- สถานะ ----- */

export const DRAFT_PR_STATUS = 'DRAFT';
export const SUBMITTED_PR_STATUS = 'SUBMITTED';
export const APPROVED_PR_STATUS = 'APPROVED';
export const CONVERTED_TO_PO_PR_STATUS = 'CONVERTED_TO_PO';
export const CANCELLED_PR_STATUS = 'CANCELLED';

/**
 * ห้าค่าที่ CHECK constraint ฝั่งเซิร์ฟเวอร์รับ ตามลำดับของ lifecycle
 *
 * `CONVERTED_TO_PO` อยู่ในชุดนี้เพราะ **หน้าจอต้องอ่านมันออก** ไม่ใช่เพราะหน้าจอนี้
 * เขียนมันได้: ไม่มี route ใดใน P2-2a เขียนค่านี้ ใบขอซื้อไปถึงสถานะนั้นได้ทางเดียว
 * คือถูกใบสั่งซื้อ (P2-2b) ดึงไปใช้ — และเมื่อถึงแล้วมันเป็นสถานะปลายทางที่ไม่มีปุ่ม
 * ใดบนหน้าจอนี้กระทำต่อได้อีก เท่ากับ `APPROVED` และ `CANCELLED`
 */
export const PURCHASE_REQUISITION_STATUSES = [
  DRAFT_PR_STATUS,
  SUBMITTED_PR_STATUS,
  APPROVED_PR_STATUS,
  CONVERTED_TO_PO_PR_STATUS,
  CANCELLED_PR_STATUS,
] as const;

export type PurchaseRequisitionStatus = (typeof PURCHASE_REQUISITION_STATUSES)[number];

/** ค่าที่เซิร์ฟเวอร์เติมให้ทุกใบตอนสร้าง — ไม่มีทางส่งค่าอื่นไปแทนได้ */
export const DEFAULT_PR_STATUS = DRAFT_PR_STATUS;

export function isPurchaseRequisitionStatus(
  value: string,
): value is PurchaseRequisitionStatus {
  return (PURCHASE_REQUISITION_STATUSES as readonly string[]).includes(value);
}

/* -------------------------------------------------------- ข้อจำกัดความยาว -- */

/** ตรงกับคอลัมน์จริงฝั่งเซิร์ฟเวอร์ (`modules/procurement/models.py`) */
export const PR_NUMBER_MAX_LENGTH = 20;
export const PR_NOTE_MAX_LENGTH = 255;
/** รหัสอ้างอิงที่ฟอร์มกรอก — ความยาวเดียวกับคอลัมน์ของเจ้าของข้อมูลนั้น ๆ */
export const PLANT_CODE_MAX_LENGTH = 20;
export const PRODUCT_CODE_MAX_LENGTH = 40;
export const UOM_CODE_MAX_LENGTH = 10;
export const SUPPLIER_CODE_MAX_LENGTH = 20;

/* ------------------------------------------------------------- กฎธุรกิจ --- */

/**
 * `409` — เลขใบขอซื้อนี้มีเจ้าของอยู่แล้ว **ไม่มีอะไรถูกเขียนเลย**
 *
 * ผู้ใช้แก้เองได้ทันทีด้วยการใช้เลขอื่น จึงต้องไม่ถูกยุบรวมกับ "เซิร์ฟเวอร์ทำงาน
 * ไม่สำเร็จ" ซึ่งเป็นสิ่งที่เขาทำอะไรไม่ได้เลย
 */
export const PR_NUMBER_TAKEN = 'PR_NUMBER_TAKEN';

/** `403` — โรงงานที่กรอกอยู่นอก `plant_scope` ของผู้เรียก (D-004) */
export const PLANT_OUTSIDE_SCOPE = 'PLANT_OUTSIDE_SCOPE';

/** `422` — ไม่มีสินค้ารหัสนี้ */
export const PRODUCT_NOT_FOUND = 'PRODUCT_NOT_FOUND';

/** `422` — มีสินค้ารหัสนี้แต่ยังไม่ `ACTIVE` (ยังไม่ผ่านอนุมัติ/ถูกเลิกใช้) */
export const PRODUCT_NOT_ACTIVE = 'PRODUCT_NOT_ACTIVE';

/** `422` — ไม่มีหน่วยนับรหัสนี้ */
export const UOM_NOT_FOUND = 'UOM_NOT_FOUND';

/**
 * `422` — ไม่มีผู้ขายรหัสที่เสนอไว้
 *
 * ผู้ขายที่ `BLOCKED` **ยังเสนอได้** (D-083/D-084) การห้ามออกใบสั่งซื้อให้เขาอยู่ที่
 * P2-2b ไม่ใช่ที่นี่ — กฎข้อนี้จึงแปลว่า "ไม่มีรหัสนี้ในทะเบียน" เท่านั้น
 */
export const SUGGESTED_SUPPLIER_NOT_FOUND = 'SUGGESTED_SUPPLIER_NOT_FOUND';

/** `400` — จำนวนต้องมากกว่าศูนย์ (ใบขอซื้อจำนวนศูนย์ไม่ได้ขออะไรเลย) */
export const QUANTITY_MUST_BE_POSITIVE = 'QUANTITY_MUST_BE_POSITIVE';

/** `400` — body พาฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของไปด้วย */
export const PURCHASE_REQUISITION_FIELD_IS_SERVER_OWNED =
  'PURCHASE_REQUISITION_FIELD_IS_SERVER_OWNED';

/**
 * `409` — สถานะปัจจุบันไม่ใช่สถานะที่หน้าจออ่านมาตอนโหลดรายการ
 *
 * มาพร้อม `details.current_status` เสมอ เกิดจริงเมื่อมีคนอื่นกดไปก่อนระหว่างที่
 * รายการเปิดค้างอยู่ — ทางแก้คือโหลดรายการใหม่ ไม่ใช่กดซ้ำ
 */
export const PURCHASE_REQUISITION_STATUS_CHANGED =
  'PURCHASE_REQUISITION_STATUS_CHANGED';

/**
 * `409` — ใบนี้ไม่มีเส้นทางจากสถานะปัจจุบันไปยังสถานะที่ปุ่มขอ
 *
 * มาพร้อม `details.current_status` เช่นกัน ต่างจากข้อข้างบนตรงที่ข้างบนคือ "มุมมอง
 * เก่า" ส่วนข้อนี้คือ "เส้นทางนี้ไม่มีอยู่จริง" (เช่นยกเลิกใบที่อนุมัติไปแล้ว)
 */
export const PURCHASE_REQUISITION_TRANSITION_NOT_ALLOWED =
  'PURCHASE_REQUISITION_TRANSITION_NOT_ALLOWED';

/**
 * `422` — ผู้อนุมัติต้องไม่ใช่ผู้สร้าง (D-010, segregation of duties)
 *
 * **ไม่ใช่ `403`**: ผู้กดถือ `PROCUREMENT` + APPROVE ครบจริง สิ่งที่เขาทำไม่ได้คือ
 * อนุมัติ **ใบนี้ใบเดียว** การแสดงมันเป็น "ไม่มีสิทธิ์" จะส่งเขาไปขอสิทธิ์เพิ่มกับ
 * ผู้ดูแลระบบเพื่อแก้สิ่งที่ทำงานถูกต้องอยู่แล้ว
 */
export const APPROVER_MUST_DIFFER_FROM_CREATOR = 'APPROVER_MUST_DIFFER_FROM_CREATOR';

/* ------------------------------------------------------------- resource --- */

/**
 * ใบขอซื้อหนึ่งใบตามที่ contract ระบุ
 *
 * ทั้ง **รหัสธุรกิจและ UUID** ถูกส่งมาคู่กันสำหรับโรงงาน สินค้า และผู้ขายที่เสนอ:
 * รหัสคือสิ่งที่คนอ่านออกบนหน้าจอ ส่วน id คือสิ่งที่ใบสั่งซื้อ (P2-2b) อ้าง (D-039)
 *
 * `quantity` เป็น **สตริง** ไม่ใช่ number — `NUMERIC(18, 4)` กว้างกว่าที่ IEEE-754
 * double เก็บได้ครบ การแปลงเป็น number ตรงนี้คือจุดที่หลักท้ายจะหายไปเงียบ ๆ
 */
export interface PurchaseRequisition {
  /** UUID surrogate ที่เซิร์ฟเวอร์สร้าง (D-039) — ใช้เป็น path ของ transition */
  readonly pr_id: string;
  /** business key ที่ผู้ใช้กรอกเอง unique ทั้งระบบ และแก้ไม่ได้หลังสร้าง */
  readonly pr_number: string;
  readonly plant_id: string;
  readonly plant_code: string;
  readonly product_id: string;
  readonly product_code: string;
  /** ทศนิยมที่เดินทางมาเป็นสตริงเสมอ — ห้ามแปลงเป็น number */
  readonly quantity: string;
  readonly uom_code: string;
  readonly required_date: string;
  readonly suggested_supplier_id: string | null;
  readonly suggested_supplier_code: string | null;
  readonly status: string;
  readonly created_by_employee_id: string;
  readonly submitted_at: string | null;
  readonly approved_by_employee_id: string | null;
  readonly approved_at: string | null;
  readonly cancelled_at: string | null;
  readonly note: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /v1/purchase-requisitions` — envelope มาตรฐานของทุกกลุ่ม */
export interface PurchaseRequisitionListPage {
  readonly items: readonly PurchaseRequisition[];
  readonly next_cursor: string | null;
}

/* -------------------------------------------------------------- requests -- */

/**
 * body ของ `POST /v1/purchase-requisitions`
 *
 * **มีแต่ code ไม่มี UUID** ตามคอนเวนชันข้ามกลุ่มของ contract — ผู้ใช้กรอกรหัสที่เขา
 * อ่านออก เซิร์ฟเวอร์เป็นผู้แปลงเป็น id
 *
 * สองคีย์ท้ายเป็น optional **จริง ๆ** คือหายไปทั้งคีย์เมื่อไม่ได้กรอก ไม่ใช่ส่ง
 * `null` หรือสตริงว่าง (`extra = "forbid"` + `min_length=1` ฝั่งเซิร์ฟเวอร์จะปฏิเสธ
 * สตริงว่างด้วย `400` ทั้งที่ผู้ใช้แค่ไม่ได้กรอกช่องที่ไม่บังคับ)
 */
export interface CreatePurchaseRequisitionBody {
  readonly pr_number: string;
  readonly plant_code: string;
  readonly product_code: string;
  /** สตริงเสมอ — ดูเหตุผลที่ `PurchaseRequisition.quantity` */
  readonly quantity: string;
  readonly uom_code: string;
  /** ISO-8601 ที่มี offset ติดมาด้วย — ดู `toRequiredDateIso()` ใน `procurementView` */
  readonly required_date: string;
  readonly suggested_supplier_code?: string;
  readonly note?: string;
}

/**
 * body ของ `submit`/`approve`/`cancel` — **ทั้งก้อนเป็น optional**
 *
 * `expected_status` คือสถานะที่ผู้กดเห็นอยู่บนหน้าจอตอนกด ไม่ใช่สถานะที่เขาอยากให้
 * เป็น เซิร์ฟเวอร์เทียบกับของจริงแล้วตอบ `409 PURCHASE_REQUISITION_STATUS_CHANGED`
 * เมื่อไม่ตรง — ด่านนี้คือสิ่งเดียวที่แยก "มีคนกดไปก่อนแล้ว" ออกจาก "เส้นทางนี้ไม่มี
 * อยู่จริง" ได้ และรายการที่เปิดค้างไว้บนจอเป็นมุมมองที่เก่าได้เสมอ
 */
export interface PurchaseRequisitionTransitionBody {
  readonly expected_status?: string;
}

/* ==========================================================================
 * ใบสั่งซื้อ (Purchase Order) — `docs/API_CONTRACT.md` "Purchase Order —
 * Payload Contract (D-085)"
 *
 * ครึ่งหลังของงานจัดซื้อ และเป็น **โมดูลเดียวกับใบขอซื้อ** (`PROCUREMENT`) —
 * ไม่มีรหัสโมดูลใหม่ในกลุ่มนี้ สิ่งที่ต่างคือเอกสารคนละใบ คนละ state machine และ
 * ความจริงข้อหนึ่งที่กำหนดทั้งฟอร์ม: **ใบสั่งซื้อไม่ได้ระบุว่าจะซื้ออะไร** มันรวม
 * ใบขอซื้อที่อนุมัติแล้วของผู้ขายรายเดียวเข้าด้วยกัน แล้วเติมสิ่งเดียวที่ใบขอซื้อ
 * ไม่รู้ คือ **ราคาต่อหน่วย** สินค้า/จำนวน/หน่วยถูก **คัดลอกจากใบขอซื้อโดย
 * เซิร์ฟเวอร์** และบรรทัดที่ระบุค่าเหล่านั้นซ้ำถูก **ปฏิเสธ** ไม่ใช่ถูกเมิน
 * (`400 PURCHASE_ORDER_LINE_FIELD_IS_COPIED_FROM_THE_REQUISITION`)
 * ========================================================================== */

/* ------------------------------------------------------------- สถานะ ----- */

export const DRAFT_PO_STATUS = 'DRAFT';
export const APPROVED_PO_STATUS = 'APPROVED';
export const SENT_PO_STATUS = 'SENT';
export const ACKNOWLEDGED_PO_STATUS = 'ACKNOWLEDGED';
export const PARTIALLY_RECEIVED_PO_STATUS = 'PARTIALLY_RECEIVED';
export const CLOSED_PO_STATUS = 'CLOSED';
export const CANCELLED_PO_STATUS = 'CANCELLED';

/**
 * เจ็ดค่าที่ CHECK constraint ฝั่งเซิร์ฟเวอร์รับ ตามลำดับของ lifecycle
 *
 * สองค่ากลาง (`PARTIALLY_RECEIVED`, `CLOSED`) อยู่ในชุดนี้เพราะ **หน้าจอต้องอ่านมัน
 * ออก** ไม่ใช่เพราะหน้าจอเขียนมันได้: ไม่มี route ใดใน P2-2b เขียนสองค่านี้ — งานรับ
 * เข้า (P2-3) เป็นผู้ขับ และเมื่อใบไปถึงสองสถานะนั้น **ยกเลิกไม่ได้อีกแล้ว** เพราะ
 * ของมาถึงแล้วจริง ๆ
 */
export const PURCHASE_ORDER_STATUSES = [
  DRAFT_PO_STATUS,
  APPROVED_PO_STATUS,
  SENT_PO_STATUS,
  ACKNOWLEDGED_PO_STATUS,
  PARTIALLY_RECEIVED_PO_STATUS,
  CLOSED_PO_STATUS,
  CANCELLED_PO_STATUS,
] as const;

export type PurchaseOrderStatus = (typeof PURCHASE_ORDER_STATUSES)[number];

/** ค่าที่เซิร์ฟเวอร์เติมให้ทุกใบตอนสร้าง — ไม่มีทางส่งค่าอื่นไปแทนได้ */
export const DEFAULT_PO_STATUS = DRAFT_PO_STATUS;

export function isPurchaseOrderStatus(value: string): value is PurchaseOrderStatus {
  return (PURCHASE_ORDER_STATUSES as readonly string[]).includes(value);
}

/* -------------------------------------------------------- ข้อจำกัดความยาว -- */

/** ตรงกับคอลัมน์จริงฝั่งเซิร์ฟเวอร์ (`modules/procurement/purchase_order.py`) */
export const PO_NUMBER_MAX_LENGTH = 20;
/** หมายเหตุของใบสั่งซื้อใช้คอลัมน์ความยาวเดียวกับของใบขอซื้อ */
export const PO_NOTE_MAX_LENGTH = PR_NOTE_MAX_LENGTH;

/* ------------------------------------------------------------- กฎธุรกิจ --- */

/**
 * `409` — เลขใบสั่งซื้อนี้มีเจ้าของอยู่แล้ว **ไม่มีอะไรถูกเขียนเลย**
 *
 * รวมถึง **ใบขอซื้อที่เลือกไว้ก็ไม่ถูก flip** — ทั้งการสร้างใบ การสร้างบรรทัด และการ
 * เปลี่ยนสถานะใบขอซื้อทุกใบอยู่ในธุรกรรมเดียว (D-085)
 */
export const PO_NUMBER_TAKEN = 'PO_NUMBER_TAKEN';

/** `422` — ไม่มีผู้ขายรหัสนี้ในทะเบียน */
export const SUPPLIER_NOT_FOUND = 'SUPPLIER_NOT_FOUND';

/**
 * `422` — ผู้ขายรายนี้ถูก **ระงับ** ออกใบสั่งซื้อให้ไม่ได้ (D-083)
 *
 * นี่คือจุดที่ D-083 ลงจริง: ใบขอซื้อ **เสนอ** ผู้ขายที่ถูกระงับได้เพราะข้อเสนอไม่
 * ผูกมัดอะไร ส่วนใบสั่งซื้อคือคำผูกมัด — และเป็นคนละเรื่องกับผู้ขายที่ถูกเลิกใช้
 */
export const SUPPLIER_IS_BLOCKED = 'SUPPLIER_IS_BLOCKED';

/**
 * `422` — ผู้ขายรายนี้ไม่ได้ `ACTIVE` แล้ว (เลิกใช้ไปแล้ว)
 *
 * **กฎพี่น้องของข้างบน ไม่ใช่กฎเดียวกัน**: ผู้ที่เห็น `SUPPLIER_IS_BLOCKED` กับผู้ขาย
 * ที่แค่ถูกเลิกใช้ จะออกไปตามหาปัญหาด้าน compliance ที่ไม่มีอยู่จริง
 */
export const SUPPLIER_IS_INACTIVE = 'SUPPLIER_IS_INACTIVE';

/** `422` — ไม่มีใบขอซื้อเลขนี้ (เป็นคำตอบเรื่อง **body** จึงไม่ใช่ `404`) */
export const PURCHASE_REQUISITION_NOT_FOUND = 'PURCHASE_REQUISITION_NOT_FOUND';

/** `422` — ใบขอซื้อใบนั้นเป็นของโรงงานอื่น ไม่ใช่โรงงานของใบสั่งซื้อ (D-004) */
export const PURCHASE_REQUISITION_IN_ANOTHER_PLANT =
  'PURCHASE_REQUISITION_IN_ANOTHER_PLANT';

/**
 * `422` — ใบขอซื้อใบนั้นยังไม่ `APPROVED` (มาพร้อม `details.current_status`)
 *
 * ครอบทั้งใบที่ยังไม่มีใครอนุมัติ ใบที่ถูกยกเลิก และ — ในกรณีที่พบบ่อยที่สุด — ใบที่
 * **ถูกสั่งซื้อไปแล้ว** ซึ่งถูก flip เป็น `CONVERTED_TO_PO` ไปตั้งแต่ใบสั่งซื้อใบก่อน
 */
export const PURCHASE_REQUISITION_NOT_APPROVED = 'PURCHASE_REQUISITION_NOT_APPROVED';

/** `422` — ใบขอซื้อใบเดียวถูกใส่ลงในใบสั่งซื้อใบนี้สองครั้ง */
export const PURCHASE_REQUISITION_LISTED_TWICE = 'PURCHASE_REQUISITION_LISTED_TWICE';

/** `409` — ใบขอซื้อใบนั้นอยู่บนใบสั่งซื้อใบอื่นแล้ว (`UNIQUE(pr_id)` กันไว้) */
export const PURCHASE_REQUISITION_ALREADY_ORDERED =
  'PURCHASE_REQUISITION_ALREADY_ORDERED';

/**
 * `400` — บรรทัดระบุสิ่งที่ **คัดลอกมาจากใบขอซื้อ** (สินค้า/จำนวน/หน่วย)
 *
 * เซิร์ฟเวอร์ **ปฏิเสธ ไม่ใช่เมินแล้วเขียนค่าของใบขอซื้อทับ** โดยเจตนา: ผู้ซื้อที่ส่ง
 * `quantity: 50` ไปกับใบขอซื้อที่ขอไว้ 200 แล้วได้ `201` กลับมา จะเชื่อว่าตัวเองสั่ง
 * ไป 50 และไม่มีใครบอกเขาเรื่องอีก 150 ที่ไม่ได้สั่ง
 */
export const PURCHASE_ORDER_LINE_FIELD_IS_COPIED_FROM_THE_REQUISITION =
  'PURCHASE_ORDER_LINE_FIELD_IS_COPIED_FROM_THE_REQUISITION';

/** `400` — body พาฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของ (สถานะ/ผู้กระทำ/เวลา) ไปด้วย */
export const PURCHASE_ORDER_FIELD_IS_SERVER_OWNED =
  'PURCHASE_ORDER_FIELD_IS_SERVER_OWNED';

/** `400` — ราคาต่อหน่วยติดลบ (คอลัมน์มี CHECK `unit_price >= 0`) */
export const UNIT_PRICE_MUST_NOT_BE_NEGATIVE = 'UNIT_PRICE_MUST_NOT_BE_NEGATIVE';

/** `409` — สถานะจริงไม่ตรงกับที่หน้าจออ่านมา (มาพร้อม `details.current_status`) */
export const PURCHASE_ORDER_STATUS_CHANGED = 'PURCHASE_ORDER_STATUS_CHANGED';

/** `409` — สถานะปัจจุบันไม่มีเส้นทางไปยังสถานะที่ปุ่มขอ (พา `current_status` มาด้วย) */
export const PURCHASE_ORDER_TRANSITION_NOT_ALLOWED =
  'PURCHASE_ORDER_TRANSITION_NOT_ALLOWED';

/**
 * `422` — กรอก "วันส่งที่ Supplier ยืนยัน" ได้เฉพาะ PO ที่อยู่สถานะ `SENT`,
 * `ACKNOWLEDGED` หรือ `PARTIALLY_RECEIVED` (D-163 ข้อ 2)
 */
export const PO_CONFIRMED_DATE_NOT_ALLOWED_IN_STATUS =
  'PO_CONFIRMED_DATE_NOT_ALLOWED_IN_STATUS';

/* ------------------------------------------------------------- resource --- */

/**
 * หนึ่งบรรทัดของใบสั่งซื้อ — **ข้อมูลของใบขอซื้อที่ถูกคัดลอกมา บวกราคา**
 *
 * `pr_number` อยู่ที่นี่เพราะมันคือเส้นด้ายกลับไปหา **เหตุผลที่สั่งซื้อ** ส่วน `pr_id`
 * คือสิ่งที่งานรับเข้าจะอ้าง (D-039)
 *
 * ทั้งสามจำนวน (`quantity`, `unit_price`, `line_amount`) เป็น **สตริง** ไม่ใช่ number
 * ด้วยเหตุผลเดียวกับ `PurchaseRequisition.quantity` — และ `line_amount` มาจาก
 * เซิร์ฟเวอร์เสมอ หน้าจอ **ไม่คูณเอง**: การคูณด้วย IEEE-754 double คือจุดที่ยอดเงิน
 * ของเอกสารผูกมัดจะเพี้ยนจากที่เซิร์ฟเวอร์เก็บไว้
 */
export interface PurchaseOrderLine {
  readonly line_no: number;
  /** `null` เมื่อบรรทัดมาจากบรรทัดของ PR แบบหลายรายการ (D-187 A) — ตอนนั้น `request_pr_id` มีค่า และ `pr_number` คือรหัสบรรทัด PR */
  readonly pr_id: string | null;
  readonly request_pr_id: string | null;
  readonly pr_number: string;
  readonly product_id: string;
  readonly product_code: string;
  /** ทศนิยมที่เดินทางมาเป็นสตริงเสมอ — คัดลอกจากใบขอซื้อ */
  readonly quantity: string;
  readonly uom_code: string;
  readonly unit_price: string;
  /** `quantity × unit_price` ที่เซิร์ฟเวอร์คำนวณ — หน้าจอไม่คำนวณซ้ำ */
  readonly line_amount: string;
  /**
   * วันส่งที่ Supplier ยืนยัน (D-163 ข้อ 2) — `YYYY-MM-DD` หรือ `null`
   *
   * `null` ครอบทั้ง "ยังไม่มีใครกรอก" และ "เซิร์ฟเวอร์รุ่นเก่ายังไม่มีคอลัมน์นี้" —
   * สองกรณีนี้หน้าจอแสดงเหมือนกัน (ดู `parsePurchaseOrderLine`)
   */
  readonly supplier_confirmed_date: string | null;
  /**
   * เวลาที่ผู้ขายยืนยันบรรทัดนี้ผ่าน Supplier Portal ของ Store และ portal username ที่ยืนยัน —
   * `null` เมื่อยังไม่ยืนยัน, เมื่อฝ่ายจัดซื้อแก้เงื่อนไขหลังยืนยัน หรือเมื่อเซิร์ฟเวอร์รุ่นเก่ายังไม่ส่งคีย์นี้
   */
  readonly supplier_confirmed_at: string | null;
  readonly supplier_confirmed_by: string | null;
}

/**
 * ใบสั่งซื้อหนึ่งใบตามที่ contract ระบุ — **บรรทัดมาพร้อมหัวใบเสมอ**
 *
 * ไม่ใช่คำขอที่สอง: ใบสั่งซื้อคือเอกสารหนึ่งใบ และหัวใบที่ไม่รู้ว่าข้างในมีอะไรคือ
 * คำผูกมัดที่แสดงโดยไม่มีเนื้อหา
 */
export interface PurchaseOrder {
  /** UUID surrogate ที่เซิร์ฟเวอร์สร้าง (D-039) — ใช้เป็น path ของ transition */
  readonly po_id: string;
  /** business key ที่ผู้ใช้กรอกเอง unique ทั้งระบบ และแก้ไม่ได้หลังสร้าง */
  readonly po_number: string;
  readonly plant_id: string;
  readonly plant_code: string;
  readonly supplier_id: string;
  readonly supplier_code: string;
  readonly status: string;
  readonly created_by_employee_id: string;
  readonly approved_by_employee_id: string | null;
  readonly approved_at: string | null;
  readonly sent_at: string | null;
  readonly acknowledged_at: string | null;
  readonly cancelled_at: string | null;
  readonly note: string | null;
  readonly lines: readonly PurchaseOrderLine[];
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /v1/purchase-orders` — envelope มาตรฐานของทุกกลุ่ม */
export interface PurchaseOrderListPage {
  readonly items: readonly PurchaseOrder[];
  readonly next_cursor: string | null;
}

/* -------------------------------------------------------------- requests -- */

/**
 * หนึ่งบรรทัดที่ฟอร์มส่งออกไป — **สองคีย์เท่านั้น และไม่มีที่ให้ใส่คีย์ที่สาม**
 *
 * สินค้า/จำนวน/หน่วยเป็นของใบขอซื้อ ไม่ใช่ของผู้ซื้อ (D-085) — ดู
 * `PURCHASE_ORDER_LINE_FIELD_IS_COPIED_FROM_THE_REQUISITION`
 */
export interface CreatePurchaseOrderLine {
  /** ใบขอซื้อแบบเดิม — ส่งอย่างใดอย่างหนึ่งกับ `request_line_code` (D-187 A) */
  readonly pr_number?: string;
  /** รหัสบรรทัดของ PR แบบหลายรายการที่อนุมัติแล้ว (`PR-<plant>-YYMMXXXX-NNN`) */
  readonly request_line_code?: string;
  readonly unit_price: string;
}

/**
 * body ของ `POST /v1/purchase-orders`
 *
 * **มีแต่ code ไม่มี UUID** ตามคอนเวนชันข้ามกลุ่มของ contract และ `supplier_code`
 * เป็น **ฟิลด์บังคับ** ต่างจาก `suggested_supplier_code` ของใบขอซื้อที่เว้นได้ —
 * ใบสั่งซื้อที่ไม่มีผู้ขายไม่ได้ผูกมัดกับใคร
 */
export interface CreatePurchaseOrderBody {
  readonly po_number: string;
  readonly plant_code: string;
  readonly supplier_code: string;
  readonly note?: string;
  /** อย่างน้อยหนึ่งบรรทัด — ใบที่ไม่มีบรรทัดเลยไม่ได้สั่งซื้ออะไร */
  readonly lines: readonly CreatePurchaseOrderLine[];
}

/**
 * body ของ `approve`/`send`/`acknowledge`/`cancel` — **ทั้งก้อนเป็น optional**
 *
 * ความหมายเดียวกับของใบขอซื้อ: สถานะที่ผู้กด **เห็นอยู่บนจอ** ตอนกด ซึ่งเป็นสิ่งเดียว
 * ที่แยก `PURCHASE_ORDER_STATUS_CHANGED` ออกจาก
 * `PURCHASE_ORDER_TRANSITION_NOT_ALLOWED` ได้ — ทั้งคู่เป็น `409` เท่ากัน
 */
export interface PurchaseOrderTransitionBody {
  readonly expected_status?: string;
}
